import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { apiFetch } from '../lib/api.js'
import { TwemojiImg } from '../lib/twemoji.jsx'
import SafeHtml from '../components/ui/SafeHtml.jsx'

import CircuitTrack from '../components/ui/CircuitTrack.jsx'
import GameTooltip from '../components/ui/GameTooltip.jsx'

import { getSession } from '../lib/session.js'
import { pageBg as podiumBg } from '../lib/images'
import { useSoundPlayer } from '../hooks/useSoundPlayer.js'
import { useQuestionFlow } from '../hooks/useQuestionFlow.js'

import SpeedFlame        from '../components/ui/SpeedFlame.jsx'
import TimerBar          from '../components/ui/TimerBar.jsx'
import AnswerButton      from '../components/ui/AnswerButton.jsx'
import FeedbackContent   from '../components/ui/FeedbackContent.jsx'
import BonusSlot         from '../components/ui/BonusSlot.jsx'
import BonusUseAnimation from '../components/ui/BonusUseAnimation.jsx'
import WandAnim          from '../components/ui/WandAnim.jsx'
import FeedbackForm      from '../components/ui/FeedbackForm.jsx'

// Mapping emoji d'attaque reçue → id bonus pour les sons hit_*
const ATTACK_EMOJI_TO_ID = {
  '🍌': 'banana',
  '🐚': 'shell',
  '💣': 'bomb',
  '🪝': 'hook',
  '❄️': 'freeze',
}


// ── Overlay attaque reçue (mobile : plein écran, desktop : sidebar droite, 3s) ──
function AttackReceivedOverlay({ emoji, type, pseudoAttaquant, vehiculeAttaquant, onDone }) {
  // useRef évite que le re-render du parent (polling 2s) ne recrée la fonction
  // onDone et ne resette le timer indéfiniment
  const onDoneRef = useRef(onDone)
  useEffect(() => {
    const t = setTimeout(() => onDoneRef.current(), 3000)
    return () => clearTimeout(t)
  }, [])

  return (
    <div className="fixed inset-0 lg:inset-auto lg:top-0 lg:right-0 lg:bottom-0 lg:w-96 z-40 bg-gray-900 flex flex-col items-center justify-center gap-6">
      <span
        className="text-[96px] leading-none select-none"
        style={{ animation: 'attackZoom 0.5s ease-in-out infinite alternate' }}
      >
        {emoji}
      </span>
      {type !== 'piege' && pseudoAttaquant && (
        <div className="flex flex-col items-center gap-2">
          <p className="text-white/60 text-sm">envoyé par</p>
          <p className="text-white font-semibold text-xl">{pseudoAttaquant}</p>
          {vehiculeAttaquant && (
            <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center">
              <TwemojiImg emoji={vehiculeAttaquant} size={44} />
            </div>
          )}
        </div>
      )}
    </div>
  )
}


// ── Animation lancer bonus (banane + attaques) ────────────────
// Part du slot inventaire et disparaît vers le haut.
// startPos : { x, y } en px depuis le coin haut-gauche de la fenêtre (desktop uniquement)
function BonusTossAnimation({ emoji, onDone, startPos }) {
  const onDoneRef = useRef(onDone)
  useEffect(() => {
    const t = setTimeout(() => onDoneRef.current(), 1450)
    return () => clearTimeout(t)
  }, [])
  const posStyle = startPos
    ? { left: startPos.x - 12, bottom: window.innerHeight - startPos.y - 12 }
    : { left: 16, bottom: 48 }
  return (
    <div
      className="fixed pointer-events-none z-50"
      style={{ ...posStyle, animation: 'bananaToss 1.4s ease-in forwards' }}
    >
      <span className="text-2xl">{emoji}</span>
    </div>
  )
}

function BananaTossAnimation({ onDone, startPos }) {
  return <BonusTossAnimation emoji="🍌" onDone={onDone} startPos={startPos} />
}

// ── Toaster "X touché(s)" centré sur la mini-carte ────────────
function AttackToast({ targets }) {
  const pseudos = targets.map(t => t.pseudo).join(', ')
  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
      <div className="bg-gray-900/80 rounded-2xl px-4 py-3 text-center">
        <div className="flex gap-2 justify-center mb-2">
          {targets.map((t, i) => (
            <div key={i} className="w-8 h-8 bg-white rounded-full flex items-center justify-center leading-none">
              <TwemojiImg emoji={t.type_vehicule} size={22} />
            </div>
          ))}
        </div>
        <p className="text-white text-sm font-semibold">
          {pseudos} touché{targets.length > 1 ? 's' : ''}
        </p>
      </div>
    </div>
  )
}

// ── Config podium joueur ──────────────────────────────────────
const PODIUM_CFG = {
  1: { medal: '🥇', cup: '🥇', label: 'Vainqueur', podiumH: 'h-28', bg: 'bg-amber-400/80', border: 'border-amber-300/60', rankText: 'text-amber-200', delay: 100 },
  2: { medal: '🥈', cup: '🥈', label: '2e',        podiumH: 'h-20', bg: 'bg-slate-400/80',  border: 'border-slate-300/50',  rankText: 'text-gray-200',  delay: 400 },
  3: { medal: '🥉', cup: '🥉', label: '3e',        podiumH: 'h-14', bg: 'bg-amber-700/80', border: 'border-amber-600/50', rankText: 'text-amber-300', delay: 700 },
}

function PlayerPodiumStand({ kart, rank, myId }) {
  const [visible, setVisible] = useState(false)
  const cfg = PODIUM_CFG[rank]
  const isMe = kart.id === myId

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), cfg.delay)
    return () => clearTimeout(t)
  }, [cfg.delay])

  return (
    <div
      className={`flex flex-col items-center transition-all duration-700 ease-out
        ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
    >
      <div className={`w-14 h-14 rounded-full bg-white shadow-lg flex items-center justify-center mb-2
        ${isMe ? 'ring-4 ring-yellow-300' : ''}`}>
        <TwemojiImg emoji={kart.type_vehicule} size={34} />
      </div>
      <div className={`w-24 ${cfg.podiumH} ${cfg.bg} border ${cfg.border} rounded-t-xl flex flex-col`}>
        <div className="px-1.5 pt-1.5 pb-1 text-center">
          <p className="text-white font-semibold text-[10px] leading-tight truncate">{kart.pseudo}</p>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <span className="text-2xl">{cfg.medal}</span>
        </div>
      </div>
    </div>
  )
}

// ── Récapitulatif par section ──────────────────────────────────
function RecapSection({ label, color, items }) {
  const cfg = {
    green: { bg: 'bg-green-500/15', border: 'border-green-500/30', text: 'text-green-300', dot: '🟢' },
    amber: { bg: 'bg-amber-500/15', border: 'border-amber-500/30', text: 'text-amber-300', dot: '🟡' },
    red:   { bg: 'bg-red-500/15',   border: 'border-red-500/30',   text: 'text-red-300',   dot: '🔴' },
  }[color]
  return (
    <div className={`${cfg.bg} border ${cfg.border} rounded-xl p-3`}>
      <p className={`${cfg.text} text-xs font-semibold mb-2`}>{cfg.dot} {label} ({items.length})</p>
      <ul className="space-y-1.5">
        {items.map((item, i) => (
          <li key={i} className="text-white/70 text-xs leading-snug">
            • <SafeHtml html={item.enonce} />
            {item.bonne_reponse && (
              <span className="text-white/40 block ml-3 mt-0.5">→ {item.bonne_reponse}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

// ── Écran fin de course ───────────────────────────────────────
function FinishedScreen({ kart, code, session }) {
  const [recap, setRecap]         = useState(null)
  const [positions, setPositions] = useState({ karts: [], nb_cases: 20 })
  const [showFeedback, setShowFeedback] = useState(false)
  const feedbackDone = localStorage.getItem(`feedback_done_${code}`) === '1'
  const [emailModal, setEmailModal]   = useState(false)
  const [emailInput, setEmailInput]   = useState('')
  const [emailStatus, setEmailStatus] = useState('idle') // idle | sending | sent | error

  function handleFeedbackDone() {
    localStorage.setItem(`feedback_done_${code}`, '1')
    setShowFeedback(false)
  }

  async function handleSendEmail(e) {
    e.preventDefault()
    setEmailStatus('sending')
    try {
      await apiFetch(`/api/karts/${code}/send-recap`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-session-token': session.token },
        body: JSON.stringify({ email: emailInput }),
      })
      setEmailStatus('sent')
    } catch {
      setEmailStatus('error')
    }
  }

  useEffect(() => {
    if (!session) return
    apiFetch(`/api/karts/${code}/recap`, { headers: { 'x-session-token': session.token } })
      .then(setRecap)
      .catch(() => {})
    apiFetch(`/api/games/${code}/positions`)
      .then(setPositions)
      .catch(() => {})
  }, [code, session])

  const ranked = useMemo(() => (
    [...positions.karts].sort((a, b) => {
      const aF = a.statut === 'finished'
      const bF = b.statut === 'finished'
      if (aF && !bF) return -1
      if (!aF && bF) return 1
      if (aF && bF) return new Date(a.finished_at ?? 0) - new Date(b.finished_at ?? 0)
      return ((b.tour_actuel - 1) * positions.nb_cases + b.position_case) -
             ((a.tour_actuel - 1) * positions.nb_cases + a.position_case)
    })
  ), [positions])

  const myRank = useMemo(() => {
    if (ranked.length > 0) {
      const idx = ranked.findIndex(k => k.id === kart.id)
      return idx >= 0 ? idx + 1 : null
    }
    return recap?.kart?.rang ?? null
  }, [ranked, kart.id, recap])
  const myCfg  = myRank && PODIUM_CFG[myRank]

  return (
    <div className="min-h-[100dvh] bg-gray-900 overflow-y-auto">

      {/* ── Section 1 : Hero Podium ── */}
      <div
        style={{ backgroundImage: `url(${podiumBg})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
        className="relative pt-10 pb-8 px-4"
      >
        {/* Overlay sombre léger pour lisibilité */}
        <div className="absolute inset-0 bg-black/40 pointer-events-none" />

        <div className="relative z-10">
          {/* Coupe conditionnelle si top 3 */}
          {myCfg && (
            <div className="text-center mb-6">
              <span className="text-8xl">{myCfg.cup}</span>
              <p className="text-white font-black text-3xl mt-2 drop-shadow">{myCfg.label} !</p>
            </div>
          )}

          {/* Podium top 3 */}
          {ranked.length > 0 ? (
            <div className="flex items-end justify-center gap-4">
              {ranked[1] && <PlayerPodiumStand kart={ranked[1]} rank={2} myId={kart.id} />}
              {ranked[0] && <PlayerPodiumStand kart={ranked[0]} rank={1} myId={kart.id} />}
              {ranked[2] && <PlayerPodiumStand kart={ranked[2]} rank={3} myId={kart.id} />}
            </div>
          ) : (
            <div className="flex justify-center">
              <div className="flex flex-col items-center gap-3 bg-gray-900/80 backdrop-blur-sm rounded-2xl p-8">
                <svg className="animate-spin h-8 w-8 text-white/70" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
                <p className="text-white/70 text-sm">Chargement du podium…</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Section 2 : Classement complet ── */}
      <div className="bg-gray-900 px-4 pt-6 pb-2">
        <p className="text-white/25 text-[10px] uppercase tracking-widest text-center mb-3">
          Classement complet
        </p>
        <div className="bg-gray-900/80 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden max-w-lg mx-auto">
          {ranked.length === 0 ? (
            <p className="text-white/30 text-sm text-center py-6">Chargement…</p>
          ) : (
            ranked.map((k, i) => {
              const cfg  = PODIUM_CFG[i + 1]
              const isMe = k.id === kart.id
              return (
                <div
                  key={k.id}
                  className={`flex items-center gap-3 px-4 py-3 border-b border-white/5 last:border-0
                    ${isMe ? 'bg-white/10' : ''}`}
                >
                  <span className={`text-base font-black w-8 shrink-0 ${cfg ? cfg.rankText : 'text-white/35'}`}>
                    {cfg ? cfg.medal : `${i + 1}e`}
                  </span>
                  <TwemojiImg emoji={k.type_vehicule} size={26} />
                  <span className={`font-medium text-sm truncate flex-1 ${isMe ? 'text-white' : 'text-white/70'}`}>
                    {k.pseudo}{isMe ? ' 👈' : ''}
                  </span>
                  <div className="flex items-center gap-2 shrink-0 text-xs text-white/60 font-mono">
                    <span>{k.correctes ?? 0} ✅</span>
                    <span>{k.timeouts ?? 0} ⌛</span>
                    <span>{k.incorrectes ?? 0} ❌</span>
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>

      {/* ── Section 3 : Analyse thèmes ── */}
      <div className="px-4 pt-6 pb-10 space-y-4">
        <p className="text-white/25 text-[10px] uppercase tracking-widest text-center mb-1">
          Analyse personnalisée
        </p>
        {!recap ? (
          <p className="text-white/30 text-sm text-center mt-4">Chargement…</p>
        ) : recap.sous_themes.length === 0 ? (
          <p className="text-white/30 text-sm text-center mt-4">Aucune question enregistrée.</p>
        ) : (
          recap.sous_themes.map((st, i) => (
            <div key={i} className="bg-white/5 rounded-2xl p-4">
              <h3 className="text-white font-semibold text-sm mb-3">{st.nom}</h3>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {st.maitrise.length  > 0 && <RecapSection label="Maîtrisé"  color="green" items={st.maitrise} />}
                {st.acquis.length    > 0 && <RecapSection label="Acquis"    color="amber" items={st.acquis} />}
                {st.a_reviser.length > 0 && <RecapSection label="À réviser" color="red"   items={st.a_reviser} />}
              </div>
            </div>
          ))
        )}
      </div>

      {/* ── Section 4 : Bouton e-mail recap ── */}
      {session?.token && (
        <div className="px-4 pb-4 flex justify-center">
          <button
            onClick={() => { setEmailModal(true); setEmailStatus('idle') }}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium px-5 py-3 rounded-xl transition"
          >
            <span>✉️</span>
            <span>Recevoir par mail</span>
          </button>
        </div>
      )}

      {/* ── Section 5 : Bouton avis ── */}
      {!feedbackDone && !showFeedback && (
        <div className="px-4 pb-8 flex justify-center">
          <button
            onClick={() => setShowFeedback(true)}
            className="flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white/80 hover:text-white text-sm font-medium px-5 py-3 rounded-xl transition"
          >
            <span>⭐</span>
            <span>Donner mon avis sur KartQuiz</span>
          </button>
        </div>
      )}

      {/* ── Modal feedback ── */}
      {showFeedback && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-md bg-gray-800 border border-white/10 rounded-2xl p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-white font-semibold text-base">Ton avis sur KartQuiz</h3>
              <button
                onClick={() => setShowFeedback(false)}
                className="text-white/40 hover:text-white p-1 rounded-lg hover:bg-white/10 transition"
                aria-label="Fermer"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <FeedbackForm
              source="joueur"
              code={code}
              session={session}
              onDone={handleFeedbackDone}
            />
          </div>
        </div>
      )}

      {/* ── Modal e-mail recap ── */}
      {emailModal && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={e => { if (e.target === e.currentTarget) setEmailModal(false) }}
        >
          <div className="w-full max-w-sm bg-gray-800 border border-white/10 rounded-2xl p-5 space-y-4">
            {emailStatus === 'sent' ? (
              <div className="text-center space-y-3 py-2">
                <p className="text-4xl">✅</p>
                <p className="text-white font-semibold">Mail envoyé !</p>
                <p className="text-white/50 text-sm">Vérifie ta boîte de réception.</p>
                <button
                  onClick={() => setEmailModal(false)}
                  className="w-full py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-xl transition font-medium text-sm"
                >
                  Fermer
                </button>
              </div>
            ) : (
              <form onSubmit={handleSendEmail} className="space-y-4">
                <div>
                  <h2 className="text-white font-semibold text-base">Recevoir par mail</h2>
                  <p className="text-white/50 text-sm mt-1">
                    Indique l'adresse à laquelle tu souhaites recevoir cette page de résultats.
                  </p>
                </div>
                <input
                  type="email"
                  required
                  value={emailInput}
                  onChange={e => setEmailInput(e.target.value)}
                  placeholder="ton@email.fr"
                  className="w-full bg-gray-900 border border-white/20 rounded-xl px-4 py-3 text-white placeholder:text-white/30 text-sm focus:outline-none focus:border-indigo-500 transition"
                />
                {emailStatus === 'error' && (
                  <p className="text-red-400 text-xs">Une erreur est survenue. Réessaie.</p>
                )}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setEmailModal(false)}
                    className="flex-1 py-2.5 text-white/60 border border-white/20 rounded-xl hover:bg-white/10 transition font-medium text-sm"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={emailStatus === 'sending'}
                    className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white rounded-xl transition font-medium text-sm"
                  >
                    {emailStatus === 'sending' ? 'Envoi…' : 'Envoyer'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

    </div>
  )
}

// ── Page principale ───────────────────────────────────────────
export default function GamePage() {
  const { code }    = useParams()
  const navigate    = useNavigate()
  const session     = getSession(code)

  const { playSound, muted, toggleMute } = useSoundPlayer()

  // Ref pour bloquer le timer pendant qu'un tooltip est affiché
  const tooltipActiveRef       = useRef(false)
  const welcomeTooltipShownRef = useRef(false)
  const prevVitesseRef         = useRef(1)

  // ── Cycle question/réponse/feedback (partagé avec SoloGamePage) ──
  const {
    kart, game, question, phase,
    selectedIdx, feedbackData,
    timerMax, timeLeft,
    error, gamePaused,
    bonusAnim, setBonusAnim,
    bonusSlotMachine, answerHidden, coquilleActive,
    setKart, setPhase, setGamePaused, setError,
    handleSubmit,
    phaseRef, pausedRef, frozenRef,
    bonusJustUsedRef, fetchNextQuestionRef, waitingForUnpauseRef,
    timerRef, selectionTimerRef, autoAdvanceTimerRef, submittingRef,
  } = useQuestionFlow({
    code,
    session,
    onInitError:   () => navigate(`/join/${code}`, { replace: true }),
    onNotPlaying:  () => navigate(`/lobby/${code}`, { replace: true }),
    extraPauseRef: tooltipActiveRef,
  })

  // ── État multi-joueur uniquement ──
  const [allKarts, setAllKarts]   = useState([])
  const [nbCases, setNbCases]     = useState(20)
  const [nbBonus, setNbBonus]     = useState(4)
  const [imageUrl, setImageUrl]   = useState(null)
  const [pieges, setPieges]       = useState([])
  const [frozen, setFrozen]             = useState(false)
  const [freezeSecsLeft, setFreezeSecsLeft] = useState(0)
  const [attackData, setAttackData]     = useState(null)
  const [wandAnim, setWandAnim]         = useState(false)
  const [bananaThrow, setBananaThrow]   = useState(false)
  const [attackToss, setAttackToss]     = useState(null)
  const [attackToast, setAttackToast]   = useState(null)
  const [activeTooltip, setActiveTooltip] = useState(null)
  const [tossStartPos, setTossStartPos] = useState(null)

  const desktopBonusRef = useRef(null)

  // Refs multi-joueur uniquement
  const positionPollRef     = useRef(null)
  const lastAttaqueTsRef    = useRef(null)
  const freezeTimerRef      = useRef(null)
  const playSoundRef        = useRef(playSound)
  const soundEnabledRef     = useRef(true)

  useEffect(() => { playSoundRef.current = playSound }, [playSound])
  useEffect(() => { soundEnabledRef.current = game?.config?.sons_actifs !== false }, [game])

  // Verrouille le scroll du body sur mobile pendant la course (évite le décalage viewport au refresh sur Android Chrome)
  // Relâché dès que la partie est terminée (podium + analyse)
  useEffect(() => {
    if (phase === 'done') {
      document.body.style.overflow = ''
      return
    }
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [phase])

  // Redirige si pas de session
  useEffect(() => {
    if (!session) navigate(`/join/${code}`, { replace: true })
  }, [])

  // Compte à rebours gel
  useEffect(() => {
    const expiry = kart?.effet_expire_le
    if (!frozen || !expiry) { setFreezeSecsLeft(0); return }
    const update = () =>
      setFreezeSecsLeft(Math.max(0, Math.ceil((new Date(expiry) - Date.now()) / 1000)))
    update()
    const id = setInterval(update, 1000)
    return () => clearInterval(id)
  }, [frozen, kart?.effet_expire_le])

  // Polling des positions + détection attaques/gel
  useEffect(() => {
    if (!session) return
    async function pollPositions() {
      try {
        const data = await apiFetch(`/api/games/${code}/positions`)
        setAllKarts(data.karts)
        setNbCases(data.nb_cases)
        setNbBonus(data.nb_bonus)
        setPieges(data.pieges ?? [])
        if (data.image_url) setImageUrl(data.image_url)

        if (data.statut === 'finished' && phaseRef.current !== 'done') {
          clearInterval(timerRef.current)
          clearTimeout(selectionTimerRef.current)
          clearTimeout(autoAdvanceTimerRef.current)
          clearInterval(positionPollRef.current)
          phaseRef.current = 'done'
          setPhase('done')
          return
        }

        const wasPaused = pausedRef.current
        const paused    = data.statut === 'paused'
        pausedRef.current = paused
        setGamePaused(paused)

        if (wasPaused && !paused && waitingForUnpauseRef.current && phaseRef.current !== 'done') {
          waitingForUnpauseRef.current = false
          fetchNextQuestionRef.current?.()
        }

        const myKartData = data.karts.find(k => k.id === kart?.id)
        if (!myKartData) return

        setKart(prev => {
          if (!prev) return prev
          const merged = { ...prev, ...myKartData }
          if (bonusJustUsedRef.current) merged.bonus_inventaire = prev.bonus_inventaire
          return merged
        })

        const isFrozen = myKartData.effet_actif === 'freeze' &&
                         myKartData.effet_expire_le &&
                         new Date(myKartData.effet_expire_le) > new Date()
        frozenRef.current = isFrozen
        setFrozen(isFrozen)
        if (!isFrozen) clearTimeout(freezeTimerRef.current)

        const att = myKartData.attaque_recue
        if (att && att.ts && att.ts !== lastAttaqueTsRef.current) {
          lastAttaqueTsRef.current = att.ts
          const currentPhase = phaseRef.current
          if (currentPhase === 'question' || currentPhase === 'feedback') {
            clearInterval(timerRef.current)
            clearTimeout(autoAdvanceTimerRef.current)
            clearTimeout(selectionTimerRef.current)
            if (currentPhase === 'question') submittingRef.current = false
            phaseRef.current = 'attacked'
            setPhase('attacked')
          }
          setAttackData({
            emoji:             att.emoji,
            type:              att.type ?? 'direct',
            pseudoAttaquant:   att.pseudo_attaquant ?? null,
            vehiculeAttaquant: att.vehicule_attaquant ?? null,
          })
          const hitId = ATTACK_EMOJI_TO_ID[att.emoji]
          if (hitId && soundEnabledRef.current) playSoundRef.current('hit_' + hitId)
        }
      } catch (_) {}
    }
    pollPositions()
    positionPollRef.current = setInterval(pollPositions, 2000)
    return () => clearInterval(positionPollRef.current)
  }, [code, kart?.id])

  // Rang du joueur parmi tous
  const rank = useMemo(() => {
    if (!kart || !allKarts.length) return null
    const sorted = [...allKarts].sort((a, b) =>
      ((b.tour_actuel - 1) * nbCases + b.position_case) -
      ((a.tour_actuel - 1) * nbCases + a.position_case)
    )
    return sorted.findIndex(k => k.id === kart.id) + 1
  }, [kart, allKarts, nbCases])

  function handleAttackOverlayDone() {
    setAttackData(null)
    fetchNextQuestionRef.current?.(null, game)
  }

  async function handleUseBonus() {
    if (!kart?.bonus_inventaire) return
    const bonusUsed = kart.bonus_inventaire
    const emoji     = bonusUsed.emoji

    // Position de départ des animations de toss (depuis le BonusSlot en desktop)
    let startPos = null
    if (window.innerWidth >= 1024 && desktopBonusRef.current) {
      const rect = desktopBonusRef.current.getBoundingClientRect()
      startPos = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }
    }
    setTossStartPos(startPos)

    // Déclencher le son sur le geste utilisateur (débloque l'AudioContext iOS)
    if (soundEnabledRef.current) playSound('use_' + bonusUsed.id)

    // Optimistic : vider l'inventaire immédiatement + armer la garde anti-race polling
    bonusJustUsedRef.current = true
    setKart(prev => prev ? { ...prev, bonus_inventaire: null } : prev)

    if (bonusUsed.id === 'wand') {
      clearInterval(timerRef.current)
      clearTimeout(selectionTimerRef.current)
      clearTimeout(autoAdvanceTimerRef.current)
      submittingRef.current = true
      setWandAnim(true)
      apiFetch(`/api/karts/${code}/use-bonus`, {
        method:  'POST',
        headers: { 'x-session-token': session.token },
        body:    JSON.stringify({ question_id: question?.id }),
      }).then(result => setKart(prev => prev ? { ...prev, ...result.kart } : result.kart))
        .catch(() => {})
        .finally(() => { bonusJustUsedRef.current = false })
      setTimeout(() => {
        setWandAnim(false)
        fetchNextQuestionRef.current?.(null, game)
      }, 1000)
      return
    }

    if (bonusUsed.id === 'banana') setBananaThrow(true)
    if (['shell', 'bomb', 'hook'].includes(bonusUsed.id)) setAttackToss(emoji)
    try {
      const result = await apiFetch(`/api/karts/${code}/use-bonus`, {
        method:  'POST',
        headers: { 'x-session-token': session.token },
      })
      // Merge au lieu de replace pour ne pas écraser les champs locaux
      setKart(prev => prev ? { ...prev, ...result.kart } : result.kart)
      if (['shell', 'bomb', 'hook'].includes(bonusUsed.id)) {
        const hitTargets = (result.targets ?? [])
          .map(t => allKarts.find(k => k.id === t.id))
          .filter(Boolean)
          .map(k => ({ pseudo: k.pseudo, type_vehicule: k.type_vehicule }))
        setTimeout(() => {
          setAttackToss(null)
          if (hitTargets.length > 0) {
            setAttackToast(hitTargets)
            setTimeout(() => setAttackToast(null), 2000)
          }
        }, 1450)
      } else if (bonusUsed.id !== 'banana') {
        setBonusAnim(emoji)
      }
    } catch (e) {
      // Restaurer le bonus en cas d'échec API
      bonusJustUsedRef.current = false
      setKart(prev => prev ? { ...prev, bonus_inventaire: bonusUsed } : prev)
      if (bonusUsed.id === 'banana') setBananaThrow(false)
      if (['shell', 'bomb', 'hook'].includes(bonusUsed.id)) setAttackToss(null)
      setError(e.message)
    } finally {
      // Désarmer la garde après 3s (au-delà d'un cycle de polling complet)
      setTimeout(() => { bonusJustUsedRef.current = false }, 3000)
    }
  }

  // ── Tooltips contextuels (première occurrence de chaque mécanique) ──

  function maybeShowTooltip(id) {
    if (tooltipActiveRef.current) return
    tooltipActiveRef.current = true
    setActiveTooltip(id)
  }

  function dismissTooltip() {
    tooltipActiveRef.current = false
    setActiveTooltip(null)
  }

  // Bienvenue — avant la toute première question
  useEffect(() => {
    if (phase !== 'question') return
    if (welcomeTooltipShownRef.current) return
    if (localStorage.getItem('kq_tooltip_welcome')) return
    welcomeTooltipShownRef.current = true
    localStorage.setItem('kq_tooltip_welcome', '1')
    maybeShowTooltip('welcome')
  }, [phase])

  // Premier bonus reçu
  useEffect(() => {
    if (!kart?.bonus_inventaire) return
    if (phase === 'attacked') return
    if (localStorage.getItem('kq_tooltip_bonus')) return
    localStorage.setItem('kq_tooltip_bonus', '1')
    maybeShowTooltip('bonus')
  }, [kart?.bonus_inventaire])

  // Première montée en Vitesse 2
  useEffect(() => {
    if (!kart) return
    if (phase === 'attacked') return
    const v = kart.vitesse ?? 1
    if (v === 2 && prevVitesseRef.current < 2 && !localStorage.getItem('kq_tooltip_speed_v2')) {
      localStorage.setItem('kq_tooltip_speed_v2', '1')
      maybeShowTooltip('speed_v2')
    }
    prevVitesseRef.current = v
  }, [kart?.vitesse])

  // Nettoyage des refs multi uniquement (timers hook nettoyés par le hook)
  useEffect(() => () => {
    clearInterval(positionPollRef.current)
    clearTimeout(freezeTimerRef.current)
  }, [])

  if (!session) return null

  if (phase === 'done') {
    return <FinishedScreen kart={kart} code={code} session={session} />
  }

  const reponses  = question?.reponses ?? []
  const tMax      = game?.config?.timer_max ?? timerMax
  const effActif  = kart?.effet_actif ?? null
  const effExp    = kart?.effet_expire_le ?? null

  // ── Zone question partagée mobile + desktop ──────────────────
  const questionZoneContent = (
    <>
      {(phase === 'loading' || phase === 'attacked') && (
        <div className="flex-1 flex items-center justify-center text-white/50">
          Chargement…
        </div>
      )}

      {(phase === 'question' || phase === 'feedback') && question && (
        <>
          <div className={phase !== 'question' ? 'invisible' : ''}>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-white/40">
                Temps restant
                {coquilleActive && <span className="text-orange-400 ml-1">🐚 ×½</span>}
              </span>
              <span className="text-white/40">{timeLeft}s</span>
            </div>
            <TimerBar timeLeft={timeLeft} timerMax={coquilleActive ? Math.max(Math.round(tMax * 0.5), 3) : tMax} />
          </div>

          <div className="flex-1 flex flex-col items-center justify-center px-2 min-h-0">
            {question.image_url && (
              <img src={question.image_url} alt="" className="w-full max-h-28 object-contain rounded-xl mb-2 mx-auto" />
            )}
            <p className="text-white text-base font-medium text-center leading-snug"><SafeHtml html={question.enonce} /></p>
          </div>

          <div className="shrink-0 relative pb-1">
            <div className={`space-y-2 [@media(max-height:700px)]:space-y-1 ${phase !== 'question' || answerHidden ? 'invisible' : ''}`}>
              {reponses.map((r, i) => {
                const state  = i === selectedIdx ? 'selected' : null
                const locked = selectedIdx !== null || frozen
                return (
                  <AnswerButton
                    key={i}
                    index={i}
                    text={r.texte ?? r}
                    state={state}
                    disabled={locked}
                    onClick={() => !locked && handleSubmit(i, question)}
                  />
                )
              })}
            </div>
            {phase === 'feedback' && feedbackData && (
              <div className="absolute inset-0 flex flex-col pb-1">
                <FeedbackContent
                  correcte={feedbackData.correcte}
                  isTimeout={feedbackData.isTimeout}
                  bonneReponseTexte={feedbackData.bonneReponseTexte}
                />
              </div>
            )}
          </div>
        </>
      )}

      {error && (
        <div className="text-orange-300 text-sm text-center bg-orange-500/20 rounded-xl py-2 px-3 flex flex-col items-center gap-2">
          <span>{/fetch/i.test(error) ? 'Connexion perdue' : error}</span>
          {/fetch/i.test(error) && (
            <button onClick={() => window.location.reload()} className="text-orange-200 underline font-medium">
              Reconnecter
            </button>
          )}
        </div>
      )}
    </>
  )

  // ── Circuit desktop : viewBox dynamique + pan centré joueur ──
  const circuitContentDesktop = (
    <>
      <CircuitTrack
        nbCases={nbCases}
        nbBonus={nbBonus}
        karts={allKarts}
        kartSize={24}
        myKartId={kart?.id ?? null}
        pieges={pieges}
        autoPan={true}
        imageUrl={imageUrl}
        className="absolute inset-0 w-full h-full"
      />
      {attackToast && <AttackToast targets={attackToast} />}
    </>
  )

  // ── Circuit mobile ────────────────────────────────────────────
  const circuitContent = (
    <>
      <CircuitTrack
        nbCases={nbCases}
        nbBonus={nbBonus}
        karts={allKarts}
        kartSize={13}
        myKartId={kart?.id ?? null}
        pieges={pieges}
        imageUrl={imageUrl}
        className="absolute inset-0 w-full h-full"
      />
      {attackToast && <AttackToast targets={attackToast} />}
      <button
        onClick={toggleMute}
        aria-label={muted ? 'Activer le son' : 'Couper le son'}
        className="absolute top-2 right-2 z-10 text-xl leading-none text-white/70 hover:text-white active:scale-90 transition-all select-none"
      >
        {muted ? '🔇' : '🔊'}
      </button>
    </>
  )

  return (
    <div className="h-[100dvh] bg-gray-900 overflow-hidden">

      {/* ── MOBILE layout (< lg) ── */}
      <div className="flex flex-col h-full lg:hidden">

        <div className="relative shrink-0 h-52 [@media(max-height:600px)]:h-36 overflow-hidden">
          {circuitContent}
        </div>

        <main className={`relative flex-1 flex flex-col px-4 pb-2 gap-3 min-h-0 overflow-hidden transition-opacity ${frozen ? 'opacity-50' : ''}`}>
          {questionZoneContent}
        </main>

        <div className="shrink-0 bg-black/40 flex items-center px-4 py-2 gap-3">
          <BonusSlot
            bonus={kart?.bonus_inventaire ?? null}
            effetActif={effActif}
            effetExpireLe={effExp}
            frozen={frozen || !!attackToss}
            onUse={handleUseBonus}
            slotMachine={bonusSlotMachine}
          />
          <div className="flex-1 text-center">
            {rank != null ? (
              <div className="flex items-baseline justify-center gap-1">
                <span className="text-3xl font-black text-white leading-none">P{rank}</span>
                <span className="text-white/35 text-sm font-medium">/{allKarts.length}</span>
              </div>
            ) : (
              <span className="text-white/20 text-lg">—</span>
            )}
          </div>
          <div className="flex items-center shrink-0">
            {kart && <SpeedFlame vitesse={kart.vitesse ?? 1} />}
          </div>
        </div>
      </div>

      {/* ── DESKTOP layout (>= lg) ── */}
      <div className="hidden lg:flex h-full">

        <div className="flex-1 relative">
          {circuitContentDesktop}
        </div>

        <aside className="w-96 flex flex-col bg-gray-900/95 border-l border-white/10 overflow-hidden">

          {/* Header : véhicule + pseudo | rang + vitesse + mute */}
          <div className="shrink-0 flex items-center justify-between px-5 py-4 border-b border-white/10">
            <div className="flex items-center gap-2">
              {kart?.type_vehicule && <TwemojiImg emoji={kart.type_vehicule} size={28} />}
              <div className="text-white font-semibold leading-tight">{kart?.pseudo ?? '…'}</div>
            </div>
            <div className="flex items-center gap-3">
              {rank != null && (
                <div className="flex items-baseline gap-0.5">
                  <span className="text-xl font-black text-white leading-none">P{rank}</span>
                  <span className="text-white/35 text-xs font-medium">/{allKarts.length}</span>
                </div>
              )}
              {kart && <SpeedFlame vitesse={kart.vitesse ?? 1} />}
              <button
                onClick={toggleMute}
                aria-label={muted ? 'Activer le son' : 'Couper le son'}
                className="p-2 rounded-xl text-white/60 hover:text-white hover:bg-white/10 transition text-lg leading-none select-none"
              >
                {muted ? '🔇' : '🔊'}
              </button>
            </div>
          </div>

          {/* Zone question */}
          <div className={`relative flex-1 min-h-0 overflow-hidden flex flex-col px-5 py-4 gap-3 transition-opacity ${frozen ? 'opacity-50' : ''}`}>
            {questionZoneContent}
          </div>

          {/* Footer : BonusSlot */}
          <div className="shrink-0 flex justify-center px-5 py-4 border-t border-white/10">
            <div ref={desktopBonusRef}>
              <BonusSlot
                bonus={kart?.bonus_inventaire ?? null}
                effetActif={effActif}
                effetExpireLe={effExp}
                frozen={frozen || !!attackToss}
                onUse={handleUseBonus}
                slotMachine={bonusSlotMachine}
              />
            </div>
          </div>
        </aside>
      </div>

      {/* ── Overlay attaque reçue (fixed : mobile inset-0, desktop w-96 à droite) ── */}
      {attackData && (
        <AttackReceivedOverlay
          emoji={attackData.emoji}
          type={attackData.type}
          pseudoAttaquant={attackData.pseudoAttaquant}
          vehiculeAttaquant={attackData.vehiculeAttaquant}
          onDone={handleAttackOverlayDone}
        />
      )}

      {/* ── Bulles explicatives (z-40) ── */}
      {activeTooltip === 'welcome' && (
        <GameTooltip
          emoji="🏁"
          title="C'est parti !"
          message={"📊 La barre en haut = ton compte à rebours\n🔤 A/B/C/D = tes réponses\n🎁 La case en bas à gauche = tes bonus"}
          buttonText="Compris, je joue ! 🏎"
          arrowSide="center"
          onDismiss={dismissTooltip}
        />
      )}
      {activeTooltip === 'bonus' && (
        <GameTooltip
          emoji={kart?.bonus_inventaire?.emoji ?? '🎁'}
          title="Tu as un bonus !"
          message={`Tu viens de ramasser un ${kart?.bonus_inventaire?.emoji ?? 'bonus'} !\nClique sur la case en bas à gauche pour l'utiliser au bon moment — ou garde-le pour plus tard.`}
          buttonText="J'ai compris ! 👍"
          arrowSide="left"
          onDismiss={dismissTooltip}
        />
      )}
      {activeTooltip === 'speed_v2' && (
        <GameTooltip
          emoji="🔥🔥"
          title="Vitesse 2 !"
          message={"2 bonnes réponses d'affilée = tu avances 2 cases à chaque fois !\nContinue sur ta lancée pour passer en 🔥🔥🔥 Vitesse 3.\nUne seule erreur ? Retour en 🔥 Vitesse 1."}
          buttonText="En route ! 🚀"
          arrowSide="right"
          onDismiss={dismissTooltip}
        />
      )}

      {/* ── Overlay gel (z-30) ── */}
      {frozen && (
        <div className="fixed inset-0 z-30 pointer-events-none border-4 border-cyan-300/70">
          <div className="absolute inset-0 bg-cyan-950/25" />
          <div className="absolute top-2 left-1/2 -translate-x-1/2 bg-cyan-900/90 text-cyan-100 text-sm font-semibold px-4 py-1.5 rounded-full flex items-center gap-2 shadow-lg">
            <span>❄️</span>
            <span>Gelé !{freezeSecsLeft > 0 ? ` ${freezeSecsLeft}s` : ''}</span>
          </div>
        </div>
      )}

      {/* ── Overlay pause (z-50) ── */}
      {gamePaused && (
        <div className="fixed inset-0 z-50 bg-gray-900/90 flex flex-col items-center justify-center gap-3">
          <span className="text-6xl">⏸</span>
          <p className="text-white font-semibold text-xl">Partie en pause</p>
          <p className="text-white/60 text-sm">En attente de l'hôte…</p>
        </div>
      )}

      {/* ── Animations ── */}
      {bonusAnim   && <BonusUseAnimation emoji={bonusAnim} onDone={() => setBonusAnim(null)} />}
      {wandAnim    && <WandAnim onDone={() => setWandAnim(false)} />}
      {bananaThrow && <BananaTossAnimation onDone={() => setBananaThrow(false)} startPos={tossStartPos} />}
      {attackToss  && <BonusTossAnimation emoji={attackToss} onDone={() => setAttackToss(null)} startPos={tossStartPos} />}

    </div>
  )
}
